import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  History,
  ChevronRight,
  Clock,
  X,
  MoreVertical,
  CopyPlus,
  Trash2,
  Download,
  Link2,
  SquarePen,
  ArrowLeft,
} from 'lucide-react';
import * as Sentry from '@sentry/react';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { LoadingSpinner } from '@/components/loading-spinner';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import {
  listWorkflowRuns,
  WorkflowRun,
} from '@/services/workflow/list-workflow-runs';
import {
  getWorkflowRun,
  WorkflowRunWithResults,
} from '@/services/workflow/get-workflow-run';
import {
  getRunEvents,
  type WorkflowEvent,
} from '@/services/workflow/get-run-events';
import { getRunsCotAvailability } from '@/services/workflow/get-runs-cot-availability';
import { mapThinkingEvents } from '@/utils/map-thinking-events';
import { formatRunExport, type ExportFormat } from '@/utils/format-run-export';
import { deleteWorkflowRun } from '@/services/workflow/delete-workflow-run';
import { saveRunAsNewWorkflow } from '@/services/workflow/save-run-as-new-workflow';
import { useOrgStore } from '@/stores/org-store';
import { useNodeStore } from '@/stores/node-store';
import { useWorkflowStore } from '@/stores/workflow-store';
import { useNotification } from '@/components/notifications';
import { CustomNode } from '@/types/node-types';
import {
  loadRunData,
  applyRunToCanvas,
} from '@/pages/workflow/helper/load-run-data';
import { RUN_ID_PARAM } from '@/constants/workflow-editor';
import { cn } from '@/lib/utils';
import { websocketService } from '@/services/websocket/websocket-service';
import { WorkflowEventTypes, NodeStatusEvent } from '@/types/workflow-types';
import { SaveRunAsWorkflowModal } from '@/components/save-run-as-workflow-modal';
import { SaveRunAsNewWorkflowInputModal } from '@/components/save-run-as-new-workflow-input-modal';
import { ConfirmationModal } from '@/components/confirmation-modal';
import { isSameDay } from 'date-fns';
import {
  formatDateTime,
  formatTime,
  formatDayLabel,
} from '@/utils/format-date';

interface PastRunsSidebarProps {
  workflowId: string;
  isOpen: boolean;
  onClose: () => void;
  isExecuting?: boolean;
  selectedRunId?: string | null;
  /**
   * Notify parent of selection. `sessionId` is the agentic-backend
   * session_id for the run — needed to scope eval results stored in
   * OpenSearch under `workflow_run_id`.
   */
  onSelectRun?: (
    runId: string | null,
    sessionId?: string | null,
    createdAt?: string | null,
  ) => void;
  onLoadingChange?: (isLoading: boolean) => void;
  /**
   * Reconnect-to-live-run callback.  Invoked when the user clicks
   * a past-run entry whose ``status`` is still ``'running'`` or
   * ``'paused'`` -- typical scenario: the user accidentally
   * refreshed mid-run (or paused, walked away, came back later)
   * and now wants to re-attach to live updates rather than open
   * the run read-only.
   *
   * The sidebar finishes hydrating the canvas (nodes, edges,
   * synthesised events from node_results) and then hands off to
   * this callback, which is responsible for the editor-local
   * pieces only the editor can do: setting ``isExecutingWorkflow``,
   * ``isPaused``, ``pauseState``, taking a fresh ``pauseSnapshot``
   * (so subsequent edit-and-resume diffs work post-refresh), and
   * re-attaching the WebSocket to the run's session.
   *
   * If absent or rejected, the sidebar falls back to the
   * historical-view path so the run is still openable read-only.
   */
  onReconnectToLiveRun?: (run: WorkflowRunWithResults) => Promise<void> | void;
  onViewCurrentRun?: () => void;
  /**
   * Sessions for runs the user started that may not yet appear in the
   * API.  The sidebar derives "running now" placeholders from this
   * list — one temp entry per session whose ``session_id`` hasn't
   * appeared in the fetched ``runs`` list yet.
   */
  pendingRunSessions?: Array<{ sessionId: string; startedAt: string }>;
}

export const PastRunsSidebar: React.FC<PastRunsSidebarProps> = ({
  workflowId,
  isOpen,
  onClose,
  isExecuting = false,
  selectedRunId: externalSelectedRunId,
  onSelectRun: externalOnSelectRun,
  onLoadingChange,
  onReconnectToLiveRun,
  onViewCurrentRun,
  pendingRunSessions,
}) => {
  const [runs, setRuns] = useState<WorkflowRun[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingRun, setIsLoadingRun] = useState(false);
  const [displayLimit, setDisplayLimit] = useState(15);

  // Modal state management
  const [runToDelete, setRunToDelete] = useState<WorkflowRun | null>(null);
  const [runToSaveAsNew, setRunToSaveAsNew] = useState<WorkflowRun | null>(
    null,
  );
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isSaveAsNewInputModalOpen, setIsSaveAsNewInputModalOpen] =
    useState(false);
  const [isSaveAsNewSuccessModalOpen, setIsSaveAsNewSuccessModalOpen] =
    useState(false);
  const [newWorkflowId, setNewWorkflowId] = useState<string | null>(null);
  const [newWorkflowName, setNewWorkflowName] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isSavingAsNew, setIsSavingAsNew] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  // null = not yet fetched; Set = fetched (may be empty for pre-OpenSearch runs)
  const [cotAvailableSessionIds, setCotAvailableSessionIds] =
    useState<Set<string> | null>(null);

  const { currentOrg } = useOrgStore();
  const { setNodes, setEdges, updateNodeResult } = useNodeStore();
  const {
    setCurrentSession,
    setIsHistoricalData,
    setLastExecutionTimestamp,
    setLoadedNodeStatuses,
  } = useWorkflowStore();
  const notification = useNotification();

  // Notify parent when loading state changes
  useEffect(() => {
    onLoadingChange?.(isLoadingRun);
  }, [isLoadingRun, onLoadingChange]);

  const fetchRuns = useCallback(
    async (silent = false) => {
      if (!currentOrg?.orgid || !workflowId || workflowId === 'new') {
        return;
      }

      if (!silent) {
        setIsLoading(true);
      }

      try {
        const response = await listWorkflowRuns(
          workflowId,
          currentOrg.orgid,
          50,
        );

        if (response.detail) {
          console.error('Failed to fetch runs:', response.detail);
          return;
        }

        const allRuns = response.runs || [];
        setRuns(allRuns);

        // Fire-and-forget: check which sessions have CoT events so every
        // row in the list can show the correct enabled/disabled state for
        // the export buttons, not just the currently selected run.
        if (currentOrg?.orgid && allRuns.length > 0) {
          // Reset to null so buttons stay enabled (fail-open) while the
          // fetch is in flight — prevents stale state from a previous
          // workflow disabling buttons for the newly loaded run list.
          setCotAvailableSessionIds(null);
          getRunsCotAvailability(
            currentOrg.orgid,
            workflowId,
            allRuns.map((r) => r.session_id),
          ).then((result) => {
            if (result !== null) setCotAvailableSessionIds(result);
          });
        }
      } catch (error) {
        console.error('Error fetching workflow runs:', error);
        if (!silent) {
          notification.error('Failed to load past runs');
        }
      } finally {
        if (!silent) {
          setIsLoading(false);
        }
      }
    },
    [currentOrg?.orgid, workflowId, notification],
  );

  const unresolvedSessions = useMemo(() => {
    const apiSessionIds = new Set(runs.map((r) => r.session_id));
    return (pendingRunSessions ?? []).filter(
      ({ sessionId }) => !apiSessionIds.has(sessionId),
    );
  }, [runs, pendingRunSessions]);

  const displayedRuns = useMemo(() => {
    const tempEntries: WorkflowRun[] = unresolvedSessions.map(
      ({ sessionId, startedAt }) => ({
        id: `temp-${sessionId}`,
        workflow_id: workflowId,
        version_id: '',
        session_id: sessionId,
        run_by_id: '',
        status: 'running',
        started_at: startedAt,
        completed_at: null,
        error_message: null,
        created_at: startedAt,
        updated_at: startedAt,
        duration_seconds: 0,
        cost: null,
        error_count: 0,
      }),
    );
    return [...tempEntries, ...runs].slice(0, displayLimit);
  }, [runs, unresolvedSessions, displayLimit, workflowId]);

  const showMoreButton = useMemo(() => {
    return runs.length > displayLimit;
  }, [runs, displayLimit]);

  const handleShowMore = () => {
    setDisplayLimit((prev) => prev + 10);
  };

  useEffect(() => {
    if (isOpen) {
      fetchRuns();
      setDisplayLimit(15);
    }
  }, [isOpen, fetchRuns]);

  useEffect(() => {
    setRuns([]);
    setDisplayLimit(15);
  }, [workflowId]);

  // Subscribe to WebSocket workflow completion events
  useEffect(() => {
    if (!isOpen) return;

    const handleWorkflowEvent = async (event: NodeStatusEvent) => {
      if (
        event.event_type === WorkflowEventTypes.TERMINATION ||
        event.event_type === WorkflowEventTypes.EXECUTION_RESULT
      ) {
        if (externalOnSelectRun) {
          externalOnSelectRun(null);
        }

        try {
          await fetchRuns(true);
        } catch (error) {
          console.error(
            'Past Runs: Failed to fetch runs after completion:',
            error,
          );
        }
      } else if (event.event_type === WorkflowEventTypes.LIFECYCLE) {
        // Refetch on pause/resume so the sidebar reflects the current status
        try {
          await fetchRuns(true);
        } catch {
          // Silent — non-critical
        }
      }
    };

    // Subscribe to all WebSocket events using '*' wildcard
    const unsubscribe = websocketService.subscribe('*', handleWorkflowEvent);

    return () => {
      unsubscribe();
    };
  }, [isOpen, fetchRuns, externalOnSelectRun]);

  // Poll while unresolved sessions exist so the API list catches up
  const hasUnresolvedSessions = unresolvedSessions.length > 0;
  useEffect(() => {
    if (!hasUnresolvedSessions || !isOpen) return;
    const interval = setInterval(() => fetchRuns(true), 5000);
    return () => clearInterval(interval);
  }, [hasUnresolvedSessions, isOpen, fetchRuns]);

  const handleSelectRun = async (run: WorkflowRun) => {
    if (!currentOrg?.orgid) {
      notification.error('No organization selected');
      return;
    }

    if (externalOnSelectRun) {
      externalOnSelectRun(run.id, run.session_id ?? null, run.created_at);
    }

    setIsLoadingRun(true);

    try {
      const result = await loadRunData(workflowId, run.id, currentOrg.orgid);

      applyRunToCanvas(result, {
        setNodes,
        setEdges,
        updateNodeResult,
        setLoadedNodeStatuses,
        setCurrentSession,
        setLastExecutionTimestamp,
        setIsHistoricalData,
      });

      // Merge CoT events only after the reconnect settles: a rehydrated
      // AWAITING_INTERVENTION applied mid-handshake gets dropped by the
      // countdown monitor's disconnected-socket guard.
      const applyRunEvents = (runEvents: WorkflowEvent[]) => {
        const eventsByNode = mapThinkingEvents(runEvents, {
          includeAwaitingIntervention: result.isLiveRun,
        });
        const merged = { ...result.synthesizedStatuses };
        for (const [nodeId, nodeEvents] of Object.entries(eventsByNode)) {
          const enriched = nodeEvents.map((evt) => ({
            ...evt,
            node_display_name: result.displayNameById[nodeId] ?? '',
          }));
          merged[nodeId] = [
            ...enriched,
            ...(result.synthesizedStatuses[nodeId] ?? []),
          ];
        }
        setLoadedNodeStatuses(merged);
      };
      const runEventsPromise = getRunEvents(
        currentOrg.orgid,
        workflowId,
        run.id,
        'all',
      );

      const isLiveRun =
        result.runData.status === 'running' ||
        result.runData.status === 'paused';
      if (isLiveRun && onReconnectToLiveRun) {
        try {
          await onReconnectToLiveRun(result.runData);
          notification.success(
            result.runData.status === 'paused'
              ? `Reconnected to paused workflow from ${formatDateTime(run.created_at)}`
              : `Reconnected to live workflow from ${formatDateTime(run.created_at)}`,
          );
        } catch (reconnectError) {
          console.error(
            '[PastRunsSidebar] reconnect-to-live failed; falling back to historical view',
            reconnectError,
          );
          setIsHistoricalData(true);
          notification.warning(
            'Could not reconnect live updates; opened the run read-only.',
          );
        }
      } else {
        setIsHistoricalData(true);
      }

      runEventsPromise.then(applyRunEvents);
    } catch (error) {
      console.error('Error loading run:', error);
      notification.error('Failed to load run');
    } finally {
      setIsLoadingRun(false);
    }
  };

  // Handler for CoT export
  const handleExportCoT = useCallback(
    async (run: WorkflowRun, format: ExportFormat) => {
      if (!currentOrg?.orgid) return;
      setIsExporting(true);
      try {
        const [events, runResponse] = await Promise.all([
          getRunEvents(currentOrg.orgid, workflowId, run.id, 'all'),
          getWorkflowRun(workflowId, run.id, currentOrg.orgid),
        ]);

        const displayNameMap: Record<string, string> = {};
        const payloadNodes = (runResponse.data?.payload?.nodes ??
          []) as CustomNode[];
        for (const node of payloadNodes) {
          displayNameMap[node.id] =
            node.data?.struct?.user_config_name ??
            node.data?.struct?.display_name ??
            node.id;
        }

        const blob = formatRunExport(events, displayNameMap, format);
        const ext = format === 'json' ? 'json' : 'md';
        const filename = `run-cot-${run.id.slice(0, 8)}.${ext}`;
        const url = URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = filename;
        anchor.click();
        URL.revokeObjectURL(url);
      } catch (error) {
        console.error('Error exporting CoT:', error);
        Sentry.captureException(error, { extra: { context: 'export-cot' } });
        notification.error('Failed to export event log');
      } finally {
        setIsExporting(false);
      }
    },
    [currentOrg?.orgid, workflowId, notification],
  );

  const handleCopyRunLink = useCallback(
    async (run: WorkflowRun) => {
      const url = new URL(window.location.href);
      url.searchParams.set(RUN_ID_PARAM, run.id);
      try {
        await navigator.clipboard.writeText(url.toString());
        notification.success('Link copied to clipboard');
      } catch {
        notification.error('Failed to copy link');
      }
    },
    [notification],
  );

  // Handler for delete action
  const handleDeleteClick = (run: WorkflowRun) => {
    setRunToDelete(run);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!runToDelete || !currentOrg?.orgid) {
      notification.error('No organization selected');
      return;
    }

    setIsDeleting(true);

    try {
      const response = await deleteWorkflowRun(
        workflowId,
        runToDelete.id,
        currentOrg.orgid,
      );

      if (response?.detail) {
        notification.error(response.detail || 'Failed to delete workflow run');
      } else {
        // Success - refresh runs and show notification
        await fetchRuns(true); // Silent refresh
        notification.success(
          `Deleted run from ${formatDateTime(runToDelete.created_at)}`,
        );

        // If deleted run was selected, clear selection
        if (externalSelectedRunId === runToDelete.id && externalOnSelectRun) {
          externalOnSelectRun(null);
        }
      }
    } catch (error) {
      console.error('Error deleting workflow run:', error);
      notification.error('Failed to delete workflow run');
    } finally {
      setIsDeleting(false);
      setIsDeleteModalOpen(false);
      setRunToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setIsDeleteModalOpen(false);
    setRunToDelete(null);
  };

  // Handler for save as new workflow action - opens input modal
  const handleSaveAsNewClick = (run: WorkflowRun) => {
    setRunToSaveAsNew(run);
    setIsSaveAsNewInputModalOpen(true);
  };

  // Handler for confirming save as new workflow with name and description
  const handleConfirmSaveAsNew = async (name: string) => {
    if (!currentOrg?.orgid || !runToSaveAsNew) {
      notification.error('No organization selected');
      return;
    }

    setIsSavingAsNew(true);

    try {
      const response = await saveRunAsNewWorkflow(
        workflowId,
        runToSaveAsNew.id,
        currentOrg.orgid,
        name,
      );

      if (response?.detail) {
        notification.error(
          response.detail || 'Failed to save run as new workflow',
        );
      } else {
        // Success - close input modal and show success modal with link
        setIsSaveAsNewInputModalOpen(false);
        setRunToSaveAsNew(null);
        const newWorkflowIdFromResponse = response.data?.id?.toString() || null;
        const workflowName = name || response.data?.name || 'Untitled Workflow';

        setNewWorkflowId(newWorkflowIdFromResponse);
        setNewWorkflowName(workflowName);
        setIsSaveAsNewSuccessModalOpen(true);

        notification.success('New workflow created successfully');
      }
    } catch (error) {
      console.error('Error saving run as new workflow:', error);
      notification.error('Failed to save run as new workflow');
    } finally {
      setIsSavingAsNew(false);
    }
  };

  const handleSaveAsNewInputModalClose = () => {
    setIsSaveAsNewInputModalOpen(false);
    setRunToSaveAsNew(null);
  };

  const handleSaveAsNewSuccessModalClose = () => {
    setIsSaveAsNewSuccessModalOpen(false);
    setNewWorkflowId(null);
    setNewWorkflowName(null);
  };

  const formatDuration = (seconds: number | null) => {
    if (seconds === null || seconds === undefined) return '--';
    if (seconds < 60) return `${Math.round(seconds)}s`;
    const minutes = Math.floor(seconds / 60);
    const secs = Math.round(seconds % 60);
    if (minutes < 60) return `${minutes}m ${secs}s`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  };

  const getStatusDisplay = (status: string) => {
    // Capitalize first letter of status
    return status.charAt(0).toUpperCase() + status.slice(1);
  };

  const getStatusColor = (status: string) => {
    const lowerStatus = status.toLowerCase();

    if (lowerStatus === 'completed' || lowerStatus === 'success') {
      return 'text-green-600 dark:text-green-400';
    }

    if (lowerStatus === 'failed' || lowerStatus === 'error') {
      return 'text-destructive';
    }

    if (lowerStatus === 'running') {
      return 'text-primary';
    }

    if (lowerStatus === 'paused') {
      return 'text-amber-600 dark:text-amber-400';
    }

    // Default for stopped, pending, etc.
    return 'text-muted-foreground';
  };

  const runGroups: Array<{
    label: string;
    items: Array<{ run: WorkflowRun; index: number }>;
  }> = [];
  displayedRuns.forEach((run, index) => {
    const previousRun = index > 0 ? displayedRuns[index - 1] : null;
    if (
      !previousRun ||
      !isSameDay(new Date(previousRun.created_at), new Date(run.created_at))
    ) {
      runGroups.push({ label: formatDayLabel(run.created_at), items: [] });
    }
    runGroups[runGroups.length - 1].items.push({ run, index });
  });

  if (!isOpen) {
    return null;
  }

  return (
    <div className='flex h-full w-full flex-col bg-background'>
      {/* Header */}
      <div className='flex items-center gap-2 border-b px-3 py-3'>
        <History className='size-4' />
        <h2 className='flex-1 text-sm font-semibold'>Past Runs</h2>
        <Button
          size='icon'
          variant='ghost'
          onClick={onClose}
          className='size-6 p-0'
        >
          <X className='size-4' />
        </Button>
      </div>

      {/* Runs List */}
      <div className='flex-1 overflow-y-auto p-1.5'>
        {/* Current workflow — always pinned on top; the clear way back out of a
            past run. Highlighted when it's what you're viewing. */}
        {onViewCurrentRun && (
          <>
            <button
              type='button'
              onClick={() => {
                if (externalSelectedRunId) onViewCurrentRun();
              }}
              disabled={!externalSelectedRunId}
              aria-current={!externalSelectedRunId}
              className={cn(
                'flex w-full items-center gap-2.5 rounded-lg border px-2 py-2 text-left transition-colors',
                externalSelectedRunId
                  ? 'cursor-pointer border-primary/30 bg-primary/5 hover:border-primary hover:bg-primary/10'
                  : 'cursor-default border-transparent bg-muted',
              )}
            >
              <div
                className={cn(
                  'flex size-8 shrink-0 items-center justify-center rounded-lg',
                  externalSelectedRunId
                    ? 'bg-primary/10 text-primary'
                    : 'bg-background text-muted-foreground',
                )}
              >
                <SquarePen className='size-4' />
              </div>
              <div className='flex min-w-0 flex-1 flex-col'>
                <span className='text-xs font-semibold leading-5'>
                  Current workflow
                </span>
                <span className='text-xs leading-4 text-muted-foreground'>
                  {externalSelectedRunId
                    ? 'Return to the editable workflow'
                    : "You're editing this"}
                </span>
              </div>
              {externalSelectedRunId && (
                <ArrowLeft className='size-4 shrink-0 text-primary' />
              )}
            </button>
          </>
        )}
        {isLoading ? (
          <div className='flex items-center justify-center py-8'>
            <LoadingSpinner />
            <span className='ml-2 text-sm text-muted-foreground'>
              Loading runs...
            </span>
          </div>
        ) : runs.length === 0 && !isExecuting ? (
          <div className='py-8 text-center text-sm text-muted-foreground'>
            No past runs found
          </div>
        ) : (
          <div className='flex flex-col'>
            {runGroups.map((group, groupIndex) => (
              <React.Fragment key={group.items[0].run.id}>
                <div
                  className={cn(
                    'flex items-center gap-2.5 px-2 pb-0.5',
                    groupIndex === 0 ? 'pt-3' : 'pt-3.5',
                  )}
                >
                  <span className='h-px flex-1 bg-border' />
                  <span className='shrink-0 text-xs font-semibold uppercase tracking-wide text-foreground'>
                    {group.label}
                  </span>
                  <span className='h-px flex-1 bg-border' />
                </div>
                {group.items.map(({ run, index }) => {
                  const isTempRun = run.id.startsWith('temp-');
                  const isLiveRun =
                    run.status === 'running' || run.status === 'paused';
                  const latestTempId =
                    unresolvedSessions.length > 0
                      ? `temp-${unresolvedSessions[unresolvedSessions.length - 1].sessionId}`
                      : null;
                  const isLatestTemp = isTempRun && run.id === latestTempId;
                  const isAutoSelected =
                    !externalSelectedRunId &&
                    isExecuting &&
                    isLiveRun &&
                    index ===
                      displayedRuns.findIndex(
                        (r) => r.status === 'running' || r.status === 'paused',
                      );
                  const isSelected =
                    externalSelectedRunId === run.id ||
                    isAutoSelected ||
                    (isLatestTemp && isExecuting);
                  // Disable CoT export when the availability set is loaded and
                  // this run's session has no CoT events.  While the set is
                  // still loading (null), leave buttons enabled (fail-open).
                  const cotUnavailable =
                    cotAvailableSessionIds !== null &&
                    run.session_id != null &&
                    !cotAvailableSessionIds.has(run.session_id);
                  const exportDisabled = isExporting || cotUnavailable;
                  const runByLabel = isTempRun
                    ? null
                    : (run.run_by?.email ?? run.run_by?.name ?? null);
                  const isScheduled = run.trigger_type === 'scheduled';

                  return (
                    <div
                      key={run.id}
                      className={cn(
                        'flex items-center gap-2.5 rounded-md px-2 py-2 transition-colors',
                        isSelected ? 'bg-muted' : 'hover:bg-muted/50',
                      )}
                    >
                      <button
                        type='button'
                        onClick={() => {
                          if (isTempRun) {
                            onViewCurrentRun?.();
                            return;
                          }
                          handleSelectRun(run);
                        }}
                        disabled={isLoadingRun}
                        className='flex min-w-0 flex-1 items-center gap-2.5 text-left disabled:cursor-not-allowed disabled:opacity-50'
                      >
                        {/* Date + who started it. The email sits on its own line in
                        a min-w-0 column so it fades out with an ellipsis (full
                        value on hover) and never pushes the date or right edge. */}
                        <div className='flex min-w-0 flex-1 flex-col'>
                          <div className='flex items-center gap-1.5 leading-5'>
                            <span className='truncate text-xs font-medium text-foreground'>
                              {formatTime(run.created_at)}
                            </span>
                            {isScheduled && (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span className='flex shrink-0'>
                                    <Clock
                                      className='size-3 text-muted-foreground'
                                      aria-label='Scheduled'
                                    />
                                  </span>
                                </TooltipTrigger>
                                <TooltipContent side='top'>
                                  Scheduled
                                </TooltipContent>
                              </Tooltip>
                            )}
                          </div>
                          {runByLabel && (
                            <span
                              className='truncate text-xs leading-4 text-muted-foreground'
                              title={runByLabel}
                            >
                              {runByLabel}
                            </span>
                          )}
                        </div>

                        {/* Right edge: status word over duration, so status reads
                        without relying on the dot color alone. */}
                        <div className='flex shrink-0 flex-col text-right'>
                          <div
                            className={cn(
                              'text-xs font-semibold leading-5',
                              getStatusColor(run.status),
                            )}
                          >
                            {getStatusDisplay(run.status)}
                          </div>
                          <div className='text-xs leading-4 text-muted-foreground'>
                            {formatDuration(run.duration_seconds)}
                          </div>
                        </div>
                      </button>

                      {/* Dropdown Menu - only show for non-temp runs */}
                      {!isTempRun && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant='ghost'
                              size='icon'
                              className='size-8 shrink-0 text-muted-foreground'
                              disabled={isLoadingRun}
                              aria-label='More options'
                            >
                              <MoreVertical className='size-4' />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align='end'>
                            <DropdownMenuItem
                              onClick={() => handleCopyRunLink(run)}
                            >
                              <Link2 className='size-4 mr-2' />
                              Copy link
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => handleSaveAsNewClick(run)}
                            >
                              <CopyPlus className='size-4 mr-2' />
                              Save as new workflow
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  {/* span captures hover even when items are disabled */}
                                  <span>
                                    <DropdownMenuItem
                                      onClick={() =>
                                        handleExportCoT(run, 'json')
                                      }
                                      disabled={exportDisabled}
                                    >
                                      <Download className='size-4 mr-2' />
                                      Export Event Log as JSON
                                    </DropdownMenuItem>
                                    <DropdownMenuItem
                                      onClick={() =>
                                        handleExportCoT(run, 'markdown')
                                      }
                                      disabled={exportDisabled}
                                    >
                                      <Download className='size-4 mr-2' />
                                      Export Event Log as Markdown
                                    </DropdownMenuItem>
                                  </span>
                                </TooltipTrigger>
                                {cotUnavailable && (
                                  <TooltipContent side='left'>
                                    Event log not available for this run
                                  </TooltipContent>
                                )}
                              </Tooltip>
                            </TooltipProvider>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              onClick={() => handleDeleteClick(run)}
                            >
                              <Trash2 className='size-4 mr-2' />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </div>
                  );
                })}
              </React.Fragment>
            ))}

            {/* Show More Button */}
            {showMoreButton && (
              <Button
                onClick={handleShowMore}
                variant='ghost'
                size='sm'
                className='h-8 w-full justify-start gap-2 rounded-md px-2 text-sm'
              >
                <ChevronRight className='size-4' />
                Show more
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Modals */}
      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        handleDelete={handleDeleteConfirm}
        handleCancel={handleDeleteCancel}
        title='Delete Run'
        description={
          runToDelete
            ? `Are you sure you want to delete the run from ${formatDateTime(runToDelete.created_at)}? This action cannot be undone.`
            : ''
        }
        buttonLabel='Delete'
        isLoading={isDeleting}
      />

      <SaveRunAsNewWorkflowInputModal
        isOpen={isSaveAsNewInputModalOpen}
        onClose={handleSaveAsNewInputModalClose}
        onConfirm={handleConfirmSaveAsNew}
        isLoading={isSavingAsNew}
      />

      <SaveRunAsWorkflowModal
        isOpen={isSaveAsNewSuccessModalOpen}
        onClose={handleSaveAsNewSuccessModalClose}
        newWorkflowId={newWorkflowId}
        newWorkflowName={newWorkflowName}
      />
    </div>
  );
};
